import * as React from "react";
import Image from "next/image";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

/**
 * Renders the hero section
 * Shows the hero image, hero title and hero paragraph
 */
const Hero = () => {
  const heading1Ref = React.useRef<HTMLHeadingElement>(null);
  const paragraph1Ref = React.useRef<HTMLHeadingElement>(null);
  const paragraph2Ref = React.useRef<HTMLParagraphElement>(null);
  const imageContainerRef = React.useRef<HTMLDivElement>(null);

  useGSAP(() => {
    const refs = [heading1Ref, paragraph1Ref, paragraph2Ref];

    refs.forEach((ref, index) => {
      gsap.fromTo(
        ref.current,
        { y: "100%" },
        {
          y: 0,
          duration: 1.5,
          delay: 0.075 * index,
          ease: "power4",
        }
      );
    });

    gsap.fromTo(
      imageContainerRef.current,
      { opacity: 0, y: 40 },
      {
        opacity: 1,
        y: 0,
        delay: 0.075,
        duration: 1.7,
        ease: "power4",
      }
    );
  }, []);

  const containerStyles = `
    2xl:max-w-8xl 3xl:max-w-9xl 4xl:max-w-10xl 5xl:max-w-11xl relative z-20 mx-auto w-full max-w-full cursor-default pt-32 sm:max-w-[40rem] md:max-w-[50rem] lg:max-w-[63rem] xl:max-w-[75rem]
  `;

  const imageContainerStyles = `
    cursor-special group flex h-60 w-full items-end justify-center overflow-hidden rounded-t-[3rem] bg-[#c4a891] sm:h-72 md:h-72 md:w-44 md:rounded-t-full lg:h-80 lg:w-80 2xl:h-96 2xl:w-96
  `;

  const heroTextContainerStyles = `
    relative mb-10 flex flex-wrap items-start justify-between gap-x-5 gap-y-10 px-10 sm:px-5 md:flex-nowrap md:gap-y-0 md:pt-8 lg:gap-x-10 lg:px-0
    `;

  return (
    <section className={containerStyles}>
      <div className="noise-overlay xs:!opacity-0 absolute inset-0 !z-0 h-[50%] w-full !opacity-50" />
      <div className="3xl:py-36 4xl:py-40 5xl:py-48 relative flex w-full items-center justify-center p-16 2xl:py-32">
        <div className="hero-heading overflow-hidden">
          <h1 className="hero-text" ref={heading1Ref}>
            I&apos;m Tarun.
          </h1>
        </div>
      </div>
      <div className={heroTextContainerStyles}>
        <div className="relative z-[600] flex w-full justify-center md:w-fit lg:w-[40%]">
          <div className={imageContainerStyles} ref={imageContainerRef}>
            <div className="h-full w-full">
              <Image
                src="/images/profile.webp"
                alt="Profile"
                fill
                priority
                className="drop-shadow-4xl mt-[5%] object-contain transition duration-300 ease-in-out group-hover:scale-[1.1] md:mt-[18%] lg:mt-[10%]"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col items-start justify-start gap-y-8 md:w-full lg:w-[60%]">
          <div className="overflow-hidden">
            <p
              className="text-primary-300 font-Acorn-Bold max-w-4xl text-[clamp(1.875rem,1.301rem+2.449vw,3.95rem)] leading-[1.2]"
              ref={paragraph1Ref}
            >
              I&apos;m a self-taught Senior Frontend Engineer working globally,
              from India.
            </p>
          </div>
          <div className="overflow-hidden">
            <p
              className="text-primary-200 max-w-3xl text-base !leading-[1.8] tracking-wide md:text-lg 2xl:text-xl"
              ref={paragraph2Ref}
            >
              <span className="animate-wave inline-block origin-[70%,70%]">
                👋
              </span>{" "}
              Hi there! I&apos;m a passionate introvert who loves building
              things with code. I&apos;m very curious about AI, Saas and cleaner
              UI! This is my personal space where I share my thoughts, projects
              and a bit about my journey.
            </p>
          </div>
        </div>
      </div>
      <div
        className="xs:hidden absolute bottom-[50%] left-0 right-0 z-[400] h-24
      bg-[linear-gradient(to_top,#110348_0%,#110348_40%,#1103480d_100%)]"
      />
    </section>
  );
};

export default Hero;
